import { Link } from "react-router-dom";
import React from "react";
import { type Chat, type User, type ActivityRequest } from "../types";
import { motion } from "framer-motion";
import {
  UsersIcon,
  ClockIcon,
  Trash2Icon,
  AlertTriangleIcon,
  StarIcon,
} from "./icons/FeatherIcons";
import { toJsDate } from "../utils/toJsDate";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";
import { type ChatDraftRecord } from "../utils/chatDrafts";

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const areParticipantsEqual = (left: User[], right: User[]) => {
  if (left.length !== right.length) return false;

  return left.every((user, index) => {
    const other = right[index];
    return (
      user.id === other?.id &&
      user.username === other?.username &&
      user.profileImageUrl === other?.profileImageUrl
    );
  });
};

const getLastMessageSummary = (chat: Chat) => {
  const lastMessage = chat.messages[chat.messages.length - 1];
  return {
    count: chat.messages.length,
    senderId: lastMessage?.senderId || "",
    text: lastMessage?.text || "",
    timestamp: lastMessage?.timestamp || "",
  };
};

const areActivitiesEquivalent = (
  left?: ActivityRequest | null,
  right?: ActivityRequest | null,
) => {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.id === right.id &&
    left.isDeleted === right.isDeleted &&
    left.isPrivate === right.isPrivate &&
    left.isReported === right.isReported &&
    left.isActive === right.isActive &&
    left.dateTime === right.dateTime &&
    left.duration === right.duration &&
    left.creatorId === right.creatorId &&
    left.creator?.username === right.creator?.username &&
    left.createdAt === right.createdAt &&
    left.groupSize === right.groupSize &&
    (left.participants?.length || 0) === (right.participants?.length || 0)
  );
};

const areChatListItemPropsEqual = (
  prev: ChatListItemProps,
  next: ChatListItemProps,
) => {
  if (prev.unreadCount !== next.unreadCount) return false;
  if (prev.currentUserId !== next.currentUserId) return false;
  if (!areParticipantsEqual(prev.participants, next.participants)) return false;
  if (!areActivitiesEquivalent(prev.activity, next.activity)) return false;
  if ((prev.draft?.text || "") !== (next.draft?.text || "")) return false;
  if ((prev.draft?.updatedAt || "") !== (next.draft?.updatedAt || "")) return false;

  const prevLastMessage = getLastMessageSummary(prev.chat);
  const nextLastMessage = getLastMessageSummary(next.chat);

  return (
    prev.chat.id === next.chat.id &&
    prev.chat.activityId === next.chat.activityId &&
    prev.chat.activityTitle === next.chat.activityTitle &&
    prev.chat.activityImageUrl === next.chat.activityImageUrl &&
    prev.chat.activityStatus === next.chat.activityStatus &&
    prevLastMessage.count === nextLastMessage.count &&
    prevLastMessage.senderId === nextLastMessage.senderId &&
    prevLastMessage.text === nextLastMessage.text &&
    prevLastMessage.timestamp === nextLastMessage.timestamp
  );
};

const AvatarStack: React.FC<{
  participants: User[];
  isDirectChat: boolean;
  imageUrl?: string;
  chatId?: string;
}> = ({ participants, isDirectChat, imageUrl, chatId }) => {
  const primary = participants[0];
  const directImage = useCachedImage(
    primary?.profileImageUrl,
    primary?.username ?? "",
    primary?.id ?? "",
  );
  const groupImage = useCachedImage(
    imageUrl && chatId ? imageUrl : undefined,
    "group",
    chatId ?? "group",
  );

  if (participants.length === 0) {
    return (
      <div className="w-14 h-14 rounded-2xl bg-gray-200 flex-shrink-0"></div>
    );
  }

  if (isDirectChat && participants.length === 1) {
    return (
      <img
        onError={handleImageError}
        src={directImage}
        alt={participants[0].username}
        className="w-14 h-14 rounded-2xl object-cover flex-shrink-0"
      />
    );
  }

  // Group chat avatar logic
  if (imageUrl && chatId) {
    return (
      <img
        onError={handleImageError}
        src={groupImage}
        alt="Activity Image"
        className="w-14 h-14 rounded-2xl object-cover flex-shrink-0"
      />
    );
  }

  // Fallback for group chat without image
  return (
    <div className="relative w-14 h-14 flex-shrink-0 flex items-center justify-center bg-gray-200 rounded-2xl">
      <UsersIcon className="w-7 h-7 text-gray-400" />
    </div>
  );
};

type ChatListItemProps = {
  chat: Chat;
  participants: User[];
  activity?: ActivityRequest | null;
  onNeedActivity?: (activityId: string) => Promise<ActivityRequest | null>;
  draft?: ChatDraftRecord;
  unreadCount: number;
  currentUserId?: string;
  returnToTab?: "groups" | "chats";
};

const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  participants,
  activity,
  onNeedActivity,
  draft,
  unreadCount,
  currentUserId,
  returnToTab,
}) => {
    const lastMessage = chat.messages[chat.messages.length - 1];
    const isUnread = unreadCount > 0;

    const isDirectChat = chat.activityId === null;
    const itemRef = React.useRef<HTMLDivElement | null>(null);

    // Lazy fetch activity details when the item becomes visible
    React.useEffect(() => {
      if (isDirectChat || activity || !chat.activityId || !onNeedActivity) return;
      const el = itemRef.current;
      if (!el) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              onNeedActivity(chat.activityId);
              observer.disconnect();
            }
          });
        },
        { rootMargin: "120px" },
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, [isDirectChat, activity, chat.activityId, onNeedActivity]);

    const isActivityExpired = React.useMemo(() => {
      if (!activity) return false;
      const now = new Date().getTime();
      const dateObj = toJsDate(activity.dateTime);
      if (!dateObj || isNaN(dateObj.getTime())) return false;
      const endTime = dateObj.getTime() + activity.duration * 60 * 1000;
      return endTime < now;
    }, [activity]);
    const isActivityDeleted = activity?.isDeleted || false;

    const organizerName = activity
      ? activity.creatorId === currentUserId
        ? "You"
        : activity.creator?.username
      : "";
    const participantCount = activity?.participants?.length ?? 0;
    const groupSize = activity?.groupSize ?? 0;

    const title =
      isDirectChat && participants.length > 0
        ? participants[0].username
        : chat.activityTitle;
    const draftText = draft?.text.trim() ?? "";
    const hasDraft = draftText.length > 0;
    const previewTimestamp = hasDraft ? draft?.updatedAt : lastMessage?.timestamp;

    const timeAgo = (dateString?: string) => {
      if (!dateString) return "";
      const date = toJsDate(dateString);
      if (!date || isNaN(date.getTime())) return "";
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const timeAgoPlain = (dateInput?: string) => {
      if (!dateInput) return "N/A";
      const date = toJsDate(dateInput);
      if (!date || isNaN(date.getTime())) return "N/A";
      const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
      let interval = seconds / 31536000;
      if (interval > 1) return `${Math.floor(interval)}y ago`;
      interval = seconds / 2592000;
      if (interval > 1) return `${Math.floor(interval)}mo ago`;
      interval = seconds / 86400;
      if (interval > 1) return `${Math.floor(interval)}d ago`;
      interval = seconds / 3600;
      if (interval > 1) return `${Math.floor(interval)}h ago`;
      interval = seconds / 60;
      if (interval > 1) return `${Math.floor(interval)}m ago`;
      return `${Math.floor(seconds)}s ago`;
    };

    return (
      <Link
        to={`/chat/${chat.id}`}
        state={{
          prefetchedParticipants: participants,
          prefetchedActivity: activity ?? null,
          returnToPath: "/chat",
          returnToState: returnToTab ? { initialTab: returnToTab } : undefined,
        }}
        className="block"
      >
        <motion.div
          className="flex items-center p-3 rounded-xl hover:bg-gray-100 transition-colors"
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
          transition={{ duration: 0.08 }}
          ref={itemRef}
        >
          <AvatarStack
            participants={participants}
            isDirectChat={isDirectChat}
            imageUrl={chat.activityImageUrl}
            chatId={chat.id}
          />

          <div className="ml-4 flex-1 overflow-hidden">
            <div className="flex justify-between items-baseline">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <p
                  className={`font-bold truncate ${isUnread ? "text-gray-900" : "text-gray-700"}`}
                >
                  {title}
                </p>
                {!isDirectChat &&
                  (activity?.isPrivate ||
                    activity?.isReported ||
                    isActivityDeleted ||
                    isActivityExpired) && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {activity?.isPrivate && (
                        <div
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                          title="Private activity"
                        >
                          🔒
                        </div>
                      )}
                      {activity?.isReported && (
                        <AlertTriangleIcon className="w-4 h-4 text-yellow-500" />
                      )}
                      {isActivityDeleted && (
                        <Trash2Icon className="w-4 h-4 text-red-500" />
                      )}
                      {isActivityExpired && (
                        <ClockIcon className="w-4 h-4 text-orange-500" />
                      )}
                    </div>
                  )}
              </div>
              {previewTimestamp && (
                <p className="text-xs text-gray-400 flex-shrink-0 ml-2">
                  {timeAgo(previewTimestamp)}
                </p>
              )}
            </div>
            {!isDirectChat && (
              <div className="mt-0.2">
                {activity ? (
                  <div className="inline-flex items-center gap-1 bg-blue-50/60 text-blue-700/80 text-[11px] font-medium px-2 py-0.5 rounded-full max-w-full">
                    <StarIcon className="w-3 h-3 text-blue-500/80" />
                    <span className="truncate">
                      Created by {organizerName || "—"} {timeAgoPlain(activity?.createdAt)} {" "}
                      ({participantCount}/{groupSize} filled)
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1 bg-gray-100 text-gray-400 text-[11px] font-medium px-2 py-0.5 rounded-full max-w-full animate-pulse">
                    <StarIcon className="w-3 h-3 text-gray-300" />
                    <span className="truncate">Loading activity…</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mt-0.5">
              <p
                className={`text-sm truncate ${isUnread ? "text-gray-600 font-medium" : "text-gray-500"}`}
              >
                {hasDraft ? (
                  <>
                    <span className="font-semibold text-red-500">Draft: </span>
                    <span>{draftText}</span>
                  </>
                ) : lastMessage ? (
                  <>
                    {lastMessage.senderId === currentUserId && (
                      <span className="font-semibold">You: </span>
                    )}
                    {lastMessage.text}
                  </>
                ) : (
                  "No messages yet"
                )}
              </p>
              {isUnread && (
                <span className="flex-shrink-0 ml-2 flex items-center justify-center">
                  {/* SVG dot indicator */}
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    aria-label="Unread"
                    className="block"
                  >
                    <circle cx="4" cy="4" r="2.5" fill="#7C3AED" />{" "}
                    {/* Use your primary color */}
                  </svg>
                </span>
              )}
            </div>
          </div>
        </motion.div>
      </Link>
    );
  };

export default React.memo(ChatListItem, areChatListItemPropsEqual);
